"use client";

import React, { useState } from "react";
import { ArrowRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import * as Dialog from "@radix-ui/react-dialog";
const services = [{
  number: "01",
  name: "MedaCube Dispensing",
  tagline: "Automated medication management that eliminates the stress of daily pill sorting.",
  features: ["Precision dose scheduling", "Automated dispensing alerts", "Tamper-proof secure storage"],
  cta: "WATCH THE OVERVIEW VIDEO",
  image: "https://seniorlivinginsights.com/wp-content/uploads/2026/06/MedaCube_Brand-with-background-for-website.webp?v=h42a9pjae2a",
  srcset: "https://seniorlivinginsights.com/wp-content/uploads/2026/06/MedaCube_Brand-with-background-for-website.webp?v=wv4pekrm69o",
  sizes: "https://seniorlivinginsights.com/wp-content/uploads/2026/06/MedaCube_Brand-with-background-for-website.webp?v=6d4zr4nkyg8",
  alt: "MedaCube medication dispenser on a clean home surface",
  imageLeft: true,
  isVideo: true
}, {
  number: "02",
  name: "Caregiver Monitoring",
  tagline: "Real-time insights to ensure your loved ones never miss a critical dose.",
  features: ["Missed dose notifications", "Caregiver dashboard access", "Medication adherence reports"],
  cta: "View monitoring features",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703060/img/caregiver_monitoring_dashboard.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703060/img/caregiver_monitoring_dashboard.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703060/img/caregiver_monitoring_dashboard.png-wvc-sizes",
  alt: "Caregiver reviewing medication adherence on a tablet",
  imageLeft: false
}, {
  number: "03",
  name: "Simplified Refills and Support",
  tagline: "Eliminate weekly pill sorting for good. Simply pour the medication bottle into the bin to make refills easy.",
  features: ["Personalized setup assistance", "Ongoing technical support", "Seamless integration planning"],
  cta: "Watch our refill process",
  image: "https://seniorlivinginsights.com/wp-content/uploads/2026/06/p14.webp?v=qp4c1bld3t",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703060/img/refilling.png",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703060/img/refilling.png",
  alt: "Professional consultation in a comfortable senior living space",
  imageLeft: true,
  isVideo: true
}];
function VideoModal({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  return <Dialog.Root open={isOpen} onOpenChange={open => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-4xl translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-2 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-lg">
          <div className="relative aspect-video w-full overflow-hidden rounded-md bg-black">
            {isOpen && <iframe src="https://player.vimeo.com/video/555839443?autoplay=1" className="absolute inset-0 h-full w-full" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title="MedaCube Video" />}
          </div>
          <Dialog.Close asChild>
            <Button variant="ghost" size="icon" className="absolute right-4 top-4 rounded-md opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground" onClick={onClose}>
              <X className="size-4" />
              <span className="sr-only">Close</span>
            </Button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>;
}
function ServiceBlock({
  service,
  index
}: {
  service: (typeof services)[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  return <div ref={ref} data-index={index} className="group relative py-16 md:py-24 lg:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
        <div className={`lg:col-span-5 ${service.imageLeft ? "lg:order-1" : "lg:order-2"}`}>
          <div className="overflow-hidden rounded-lg">
            <div className={`aspect-square w-full transition-transform duration-[1200ms] ease-out will-change-transform ${isVisible ? "translate-y-0" : "translate-y-6"} group-hover:scale-[1.03]`} style={{
            transitionProperty: "transform, opacity"
          }}>
              <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-all duration-[1400ms] ease-out group-hover:scale-[1.03] !opacity-[0.6]" />
            </div>
          </div>
        </div>

        <div className={`lg:col-span-6 ${service.imageLeft ? "lg:order-2 lg:col-start-7" : "lg:order-1 lg:col-start-1"}`}>
          <div className={`mb-6 md:mb-8 transition-all duration-[900ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            
          </div>

          <div className="overflow-hidden mb-4">
            <h3 className={`font-default text-foreground text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-tight transition-transform duration-[900ms] ease-out delay-150 ${isVisible ? "translate-y-0" : "translate-y-full"}`}>
              {service.name}
            </h3>
          </div>

          <p className={`font-default italic text-slate-text text-xl md:text-2xl leading-snug mb-8 md:mb-10 max-w-xl transition-all duration-[900ms] ease-out delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            {service.tagline}
          </p>

          <ul className={`space-y-3 mb-10 md:mb-12 transition-all duration-[900ms] ease-out delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            {service.features.map((feature, i) => <li key={i} data-index={i} className="flex items-baseline gap-3 font-default text-sm md:text-base text-foreground tracking-wide">
                <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-lg bg-primary shrink-0 translate-y-[-2px]" />
                <span>{feature}</span>
              </li>)}
          </ul>

          <div className={`transition-all duration-[900ms] ease-out delay-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            {service.isVideo ? <Button variant={index === 0 || index === 2 ? "default" : "link"} className={index === 0 || index === 2 ? "bg-primary text-primary-foreground px-8 py-4 h-auto rounded-md hover:shadow-lg transition-all duration-300 font-default text-sm md:text-base tracking-widest uppercase inline-flex items-center gap-3 group/cta cursor-pointer" : "px-0 h-auto text-foreground hover:text-primary font-default text-sm md:text-base tracking-widest uppercase no-underline hover:no-underline inline-flex items-center gap-3 group/cta cursor-pointer"} onClick={() => setIsVideoOpen(true)}>
                <ArrowRight className="size-4 transition-transform duration-500 group-hover/cta:translate-x-1" />
                <span className="relative !font-semibold">
                  {service.cta}
                  {index !== 0 && index !== 2 && <span aria-hidden="true" className="absolute left-0 -bottom-1 h-px w-0 bg-current transition-all duration-500 group-hover/cta:w-full" />}
                </span>
              </Button> : <Button asChild variant={index === 1 ? "default" : "link"} className={index === 1 ? "bg-primary text-primary-foreground px-8 py-4 h-auto rounded-md hover:shadow-lg transition-all duration-300 font-default text-sm md:text-base tracking-widest uppercase inline-flex items-center gap-3 group/cta" : "px-0 h-auto text-foreground hover:text-primary font-default text-sm md:text-base tracking-widest uppercase no-underline hover:no-underline inline-flex items-center gap-3 group/cta"}>
                
              </Button>}
          </div>
        </div>
      </div>
      <VideoModal isOpen={isVideoOpen} onClose={() => setIsVideoOpen(false)} />
    </div>;
}
export default function ComprehensiveCareSolutions() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  return <section data-section-id="4349" id="comprehensive-care-solutions" className="bg-background text-foreground py-24 md:py-32 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div ref={headerRef} className="text-center max-w-4xl mx-auto mb-20 md:mb-28">
          <div className={`mb-8 transition-all duration-[900ms] ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
            <span className="inline-flex items-center gap-3 font-default text-xs md:text-sm tracking-[0.35em] uppercase text-slate-text">
              <span aria-hidden="true" className="inline-block h-px w-10 bg-border" />
              Our Solutions
              <span aria-hidden="true" className="inline-block h-px w-10 bg-border" />
            </span>
          </div>

          <div className="overflow-hidden">
            
          </div>
        </div>

        <div>
          {services.map((service, i) => <div key={service.number} data-index={i}>
              <ServiceBlock service={service} index={i} />
              {i < services.length - 1 && <Separator className="bg-border" />}
            </div>)}
        </div>
      </div>
    </section>;
}